import React, { ReactNode, useRef, useEffect, useMemo, useState } from "react"
import Image from "next/image"

/**
 *
 * @returns Element (if it exists in the DOM)
 */
const useContainer = (id: string) => {
    const modalPortalRef = useRef<HTMLElement>()
    // Run this effect only once (empty dependencies list) to hold on to
    // the div element (if it exists).
    useEffect(() => {
        if (typeof window !== undefined) {
            modalPortalRef.current = document.getElementById(id) || undefined
        }
    }, [id])

    return modalPortalRef.current
}

const Modal: React.FunctionComponent<{
    title?: ReactNode,
    show: boolean,
    className?: string
    onDismiss?: () => void
}> = ({ title, show, className, onDismiss, children }) => {
    const [isShown, setIsShown] = useState(show)

    useEffect(() => {
        setIsShown(show)
    }, [show])

    const backdropClassName = useMemo(() => {
        return [
            isShown ? "fixed" : "hidden",
            "top-0 left-0 w-screen h-screen z-40 bg-black-500 backdrop-blur"
        ].join(" ")
    }, [isShown])

    const modalCardClassName = useMemo(() => {
        return [
            "fixed top-[50%] left-[50%] -translate-x-[50%] z-50",
            className ? className : "w-[70%] max-w-[640px]",
            "p-std bg-white-900 rounded shadow-lg",
            // Translate it way off-screen when it is not shown so we have nice
            // transition animation when isShown state changes.
            isShown ? "-translate-y-[50%]" : "translate-y-[1000%]",
            "transition", "ease-in-out", "duration-300"
        ].join(" ")
    }, [isShown, className])

    return (
        <div id="modal">
            <div id="modal.backdrop" className={backdropClassName} />
            <div id="modal.content" className={modalCardClassName}>
                <div className="flex">
                    <h2 className="flex flex-1 px-[20px] sm:px-[40px] text-headline items-center">
                        {title}
                    </h2>

                    <button id="modal.closeButton" type="button" className="min-w-[48px] min-h-[36px] mt-[12px] mr-[12px]"
                        onClick={() => {
                            setIsShown(false)
                            onDismiss && onDismiss()
                        }}>
                        <Image width="24" height="24" objectFit="contain"
                            src="/images/ic_nav_close.svg"
                            alt="Close Button" />
                    </button>
                </div>

                <div className="mt-[12px] mb-[20px] mx-[20px]">{children}</div>
            </div>
        </div>
    )
}

export default Modal
